import { type SubmitEvent, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  PlugZap,
  Trash2,
  X,
  XCircle,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import {
  type ConnectionTestResult,
  type CreateConnectionMethodRequest,
  type DeviceConnectionMethod,
  type UpdateConnectionMethodRequest,
  useCreateConnectionMethod,
  useDeleteConnectionMethod,
  useDeviceConnectionMethods,
  useTestConnections,
  useToggleConnectionMethodEnabled,
  useUpdateConnectionMethod,
} from '@/hooks/useDeviceConnectionMethods';
import { useCredentials } from '@/hooks/useCredentials';
import {
  useCreateTransportDefinition,
  useTransportDefinitions,
} from '@/hooks/useTransportDefinitions';
import { apiErrorMessage } from '@/utils/apiError';
import ConfirmationModal from './ConfirmationModal';
import CredentialSelect from './CredentialSelect';
import { credentialTypesFor, SSH_CREDENTIAL_TYPES } from './credentialTypes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { AppSheet } from '@/components/ui/app-sheet';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface ConnectionMethodsProps {
  deviceId: string;
  regionId: string;
  divisionId: string | null;
  deviceIp?: string | null;
}

const SESSION_TYPE_STYLE: Record<string, string> = {
  ssh: 'bg-brand-soft text-brand',
  telnet: 'bg-sev-5/15 text-sev-5',
  http: 'bg-sev-3/20 text-foreground',
  https: 'bg-sev-4/15 text-sev-4',
  console: 'bg-muted text-foreground',
  snmp: 'bg-primary/15 text-primary',
  netconf: 'bg-accent text-accent-foreground',
};

/// Human labels for the vendor-profile `service` role. Shown alongside the
/// session-type badge for http(s) methods, where the transport alone is
/// ambiguous: a device can serve a browsable UI and a machine API both over
/// HTTPS, and the role is the only thing that tells them apart.
const ROLE_LABEL: Record<string, string> = {
  cgi: 'Web UI',
  http: 'Web UI',
  https: 'Web UI',
  rest: 'REST',
  http_api: 'REST',
  eapi: 'eAPI',
};

/// Roles selectable for an http(s) method. Sentinel means "don't record one",
/// which leaves the role to be inferred from the vendor profile.
const NO_ROLE_SENTINEL = '__infer__';

const DEFAULT_PORTS: Record<string, number> = {
  ssh: 22,
  telnet: 23,
  http: 80,
  https: 443,
  snmp: 161,
  // NETCONF rides the SSH `netconf` subsystem on 22 by default; a dedicated
  // listener is typically 830. We default to 830 since that's the explicit
  // NETCONF port — the gateway records whichever the device actually used at
  // activation, and the user can override here.
  netconf: 830,
};

const NEW_SSH_SENTINEL = '__new_ssh__';
const NEW_TELNET_SENTINEL = '__new_telnet__';
const NO_TRANSPORT_SENTINEL = '__none__';
const NO_CRED_SENTINEL = '__none__';

export default function ConnectionMethods({
  deviceId,
  regionId,
  divisionId,
  deviceIp,
}: ConnectionMethodsProps) {
  const { user } = useAuth();
  const { data: methods, isLoading, isError } = useDeviceConnectionMethods(deviceId);
  const { data: credentials } = useCredentials(user?.organizationId);
  const { data: transports } = useTransportDefinitions(user?.organizationId);

  const createMutation = useCreateConnectionMethod(deviceId);
  const updateMutation = useUpdateConnectionMethod(deviceId);
  const deleteMutation = useDeleteConnectionMethod(deviceId);
  const toggleMutation = useToggleConnectionMethodEnabled(deviceId);
  const createTransportMutation = useCreateTransportDefinition();
  const testMutation = useTestConnections(deviceId);

  const [testResults, setTestResults] = useState<ConnectionTestResult[] | null>(null);
  const [testError, setTestError] = useState<string | null>(null);

  const handleAttemptConnection = () => {
    setTestError(null);
    setTestResults(null);
    testMutation.mutate(undefined, {
      onSuccess: (results) => { setTestResults(results); },
      onError: (err) => {
        const axiosErr = err as {
          response?: { data?: { description?: string; error?: string } };
        };
        setTestError(
          axiosErr.response?.data?.description ??
            axiosErr.response?.data?.error ??
            'Connection attempt failed to reach the gateway.',
        );
      },
    });
  };

  const [isModalActive, setIsModalActive] = useState(false);
  const [editingMethod, setEditingMethod] = useState<DeviceConnectionMethod | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [formSessionType, setFormSessionType] = useState('ssh');
  const [formHost, setFormHost] = useState('');
  const [formPort, setFormPort] = useState('');
  const [formCredentialId, setFormCredentialId] = useState(NO_CRED_SENTINEL);
  const [formTransportId, setFormTransportId] = useState(NO_TRANSPORT_SENTINEL);
  const [formPriority, setFormPriority] = useState('0');
  const [formEnabled, setFormEnabled] = useState(true);
  const [formCommand, setFormCommand] = useState('');
  const [formService, setFormService] = useState(NO_ROLE_SENTINEL);

  const [newServerName, setNewServerName] = useState('');
  const [newServerHost, setNewServerHost] = useState('');
  const [newServerPort, setNewServerPort] = useState('');
  const [newServerCredentialId, setNewServerCredentialId] = useState(NO_CRED_SENTINEL);
  // Which credential select opened the inline create dialog, so onCreated writes
  // back to the right field.

  const isEditing = editingMethod !== null;
  const isCreatingNewServer =
    formTransportId === NEW_SSH_SENTINEL || formTransportId === NEW_TELNET_SENTINEL;
  const isMutating =
    createMutation.isPending ||
    updateMutation.isPending ||
    createTransportMutation.isPending;

  const resetNewServerFields = () => {
    setNewServerName('');
    setNewServerHost('');
    setNewServerPort('');
    setNewServerCredentialId(NO_CRED_SENTINEL);
  };

  const regionTransports = (transports ?? []).filter(
    (t) => t.regionId === regionId && (t.divisionId === null || t.divisionId === divisionId),
  );
  const consoleTransports = regionTransports.filter(
    (t) => t.transportType === 'ssh_tunnel' || t.transportType === 'telnet',
  );

  const openCreateModal = () => {
    setEditingMethod(null);
    setFormSessionType('ssh');
    // Pre-fill the device's own address — the common case for a new
    // connection is "reach this device directly".
    setFormHost(deviceIp ?? '');
    setFormPort('');
    setFormCredentialId(NO_CRED_SENTINEL);
    setFormTransportId(NO_TRANSPORT_SENTINEL);
    setFormPriority('0');
    setFormEnabled(true);
    setFormCommand('');
    setFormService(NO_ROLE_SENTINEL);
    resetNewServerFields();
    setSubmitError(null);
    setIsModalActive(true);
  };

  const openEditModal = (method: DeviceConnectionMethod) => {
    setEditingMethod(method);
    setFormSessionType(method.sessionType);
    setFormHost(method.host);
    setFormPort(method.port !== null ? String(method.port) : '');
    setFormCredentialId(method.credentialId ?? NO_CRED_SENTINEL);
    const transportId = method.transportId ?? '';
    if (method.sessionType === 'console' && !transportId) {
      setFormTransportId(consoleTransports.length > 0 ? consoleTransports[0].id : NEW_SSH_SENTINEL);
    } else {
      setFormTransportId(transportId || NO_TRANSPORT_SENTINEL);
    }
    setFormPriority(String(method.priority));
    setFormEnabled(method.enabled);
    setFormCommand(method.command ?? '');
    setFormService(method.service ?? NO_ROLE_SENTINEL);
    resetNewServerFields();
    setSubmitError(null);
    setIsModalActive(true);
  };

  const closeModal = () => {
    setIsModalActive(false);
    setEditingMethod(null);
    setSubmitError(null);
  };

  const isConsole = formSessionType === 'console';
  const isSnmp = formSessionType === 'snmp';
  // Only http(s) carries an ambiguous role; every other transport is its own
  // surface, so the role picker would be noise.
  const isWebTransport = formSessionType === 'http' || formSessionType === 'https';
  const selectedTransport = transports?.find((t) => t.id === formTransportId);
  const consoleTransportIsSsh =
    selectedTransport?.transportType === 'ssh_tunnel' || formTransportId === NEW_SSH_SENTINEL;
  const availableTransports = isConsole ? consoleTransports : regionTransports;
  const showHostPort = !isConsole;

  const handleSubmit = async (e?: SubmitEvent) => {
    e?.preventDefault();
    setSubmitError(null);

    if (isCreatingNewServer && !newServerHost.trim()) {
      setSubmitError('Console server host is required.');
      return;
    }
    if (!isConsole && !formHost.trim()) {
      setSubmitError('Host is required.');
      return;
    }

    const onError = (err: Error) => {
      const axiosErr = err as {
        response?: { data?: { description?: string; error?: string } };
      };
      setSubmitError(
        axiosErr.response?.data?.description ??
          axiosErr.response?.data?.error ??
          `Failed to ${isEditing ? 'update' : 'create'} connection method.`,
      );
    };

    let resolvedTransportId: string | null =
      formTransportId === NO_TRANSPORT_SENTINEL ? null : formTransportId;
    if (isCreatingNewServer) {
      const isSsh = formTransportId === NEW_SSH_SENTINEL;
      const defaultServerPort = isSsh ? 22 : 23;
      const serverName = newServerName.trim() || `Console: ${newServerHost.trim()}`;
      try {
        const transport = await createTransportMutation.mutateAsync({
          name: serverName,
          transportType: isSsh ? 'ssh_tunnel' : 'telnet',
          host: newServerHost.trim(),
          port: newServerPort ? parseInt(newServerPort, 10) : defaultServerPort,
          credentialId:
            isSsh && newServerCredentialId !== NO_CRED_SENTINEL
              ? newServerCredentialId
              : null,
          parentTransportId: null,
          regionId,
          divisionId,
        });
        resolvedTransportId = transport.id;
      } catch (err) {
        onError(err as Error);
        return;
      }
    }

    const effectiveHost = isConsole ? '' : formHost.trim();
    const effectivePort = isConsole
      ? null
      : formPort
        ? parseInt(formPort, 10)
        : (DEFAULT_PORTS[formSessionType] ?? null);
    const resolvedCredId =
      formCredentialId === NO_CRED_SENTINEL ? null : formCredentialId;

    if (isEditing) {
      const data: UpdateConnectionMethodRequest = {
        sessionType: formSessionType,
        ...(isConsole ? {} : { host: effectiveHost, port: effectivePort }),
        credentialId: resolvedCredId,
        transportId: resolvedTransportId,
        priority: parseInt(formPriority, 10) || 0,
        enabled: formEnabled,
        command: isConsole && formCommand.trim() ? formCommand.trim() : null,
        // Only meaningful for http(s); null clears any recorded role so it
        // falls back to the vendor profile again.
        service: isWebTransport && formService !== NO_ROLE_SENTINEL ? formService : null,
      };
      updateMutation.mutate(
        { methodId: editingMethod.id, data },
        { onSuccess: () => { closeModal(); }, onError },
      );
    } else {
      const data: CreateConnectionMethodRequest = {
        sessionType: formSessionType,
        host: effectiveHost,
        port: effectivePort,
        credentialId: resolvedCredId,
        transportId: resolvedTransportId,
        priority: parseInt(formPriority, 10) || 0,
        enabled: formEnabled,
        command: isConsole && formCommand.trim() ? formCommand.trim() : null,
        // Only meaningful for http(s); null clears any recorded role so it
        // falls back to the vendor profile again.
        service: isWebTransport && formService !== NO_ROLE_SENTINEL ? formService : null,
      };
      createMutation.mutate(data, { onSuccess: () => { closeModal(); }, onError });
    }
  };

  const resolveCredentialName = (credentialId: string | null): string => {
    if (!credentialId || !credentials) return '—';
    return credentials.find((c) => c.id === credentialId)?.name ?? '—';
  };

  const resolveTransportName = (transportId: string | null): string => {
    if (!transportId || !transports) return '—';
    return transports.find((t) => t.id === transportId)?.name ?? '—';
  };


  if (isLoading) return <Skeleton className="h-32 w-full" />;
  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Failed to load connection methods.</AlertDescription>
      </Alert>
    );
  }

  const columns: readonly ResponsiveTableColumn<DeviceConnectionMethod>[] = [
    {
      key: 'type',
      label: 'Type',
      render: (m) => (
        <div className="flex items-center gap-1.5">
          <Badge
            className={cn(
              'border-0 font-normal uppercase',
              SESSION_TYPE_STYLE[m.sessionType] ?? 'bg-muted text-muted-foreground',
            )}
          >
            {m.sessionType}
          </Badge>
          {/* Two rows can share a transport, so the role is what distinguishes
              them in the list — without it a device serving J-Web and a REST
              API just shows "HTTPS" twice. */}
          {(m.sessionType === 'http' || m.sessionType === 'https') && m.service ? (
            <span className="text-xs text-muted-foreground">
              {ROLE_LABEL[m.service] ?? m.service}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'host',
      label: 'Host',
      render: (m) => (
        <code className="font-mono text-xs">{m.host || '—'}</code>
      ),
    },
    {
      key: 'port',
      label: 'Port',
      render: (m) =>
        m.port !== null ? <span className="font-mono">{m.port}</span> : '—',
      hideAt: 'mobile',
    },
    {
      key: 'command',
      label: 'Command',
      render: (m) =>
        m.command ? <code className="font-mono text-xs">{m.command}</code> : '—',
      hideAt: 'mobile',
    },
    {
      key: 'cred',
      label: 'Credential',
      render: (m) => resolveCredentialName(m.credentialId),
      hideAt: 'mobile',
    },
    {
      key: 'transport',
      label: 'Transport',
      render: (m) => resolveTransportName(m.transportId),
      hideAt: 'mobile',
    },
    {
      key: 'priority',
      label: 'Priority',
      render: (m) => <span className="font-mono">{m.priority}</span>,
      hideAt: 'mobile',
    },
    {
      key: 'enabled',
      label: 'Enabled',
      render: (m) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleMutation.mutate({ methodId: m.id, enabled: !m.enabled });
          }}
          title={m.enabled ? 'Click to disable' : 'Click to enable'}
        >
          <Badge
            className={cn(
              'border-0 font-normal cursor-pointer',
              m.enabled
                ? 'bg-sev-4/15 text-sev-4'
                : 'bg-muted text-muted-foreground',
            )}
          >
            {m.enabled ? 'Yes' : 'No'}
          </Badge>
        </button>
      ),
    },
  ];

  return (
    <section className="bg-card border border-border rounded-lg p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold">Connection methods</h2>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleAttemptConnection}
            disabled={testMutation.isPending || (methods?.length ?? 0) === 0}
            title={
              (methods?.length ?? 0) === 0
                ? 'Add a connection method first'
                : 'Try connecting to the device with each configured method'
            }
          >
            {testMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <PlugZap className="w-4 h-4" />
            )}
            {testMutation.isPending ? 'Attempting…' : 'Attempt connection'}
          </Button>
          <Button size="sm" onClick={openCreateModal}>
            <Plus className="w-4 h-4" />
            Add method
          </Button>
        </div>
      </div>

      {testError ? (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{testError}</AlertDescription>
        </Alert>
      ) : null}

      {testResults ? (
        <div className="mb-4 rounded-md border border-border bg-bg-subtle p-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-muted-foreground">
              Connection attempt results
            </p>
            <button
              type="button"
              onClick={() => { setTestResults(null); }}
              className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-accent"
              aria-label="Dismiss results"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          {testResults.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No connection methods were available to test.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {testResults.map((r) => (
                <li key={r.methodName} className="flex items-start gap-2 text-xs">
                  {r.success ? (
                    <CheckCircle2 className="w-4 h-4 shrink-0 text-sev-4" />
                  ) : (
                    <XCircle className="w-4 h-4 shrink-0 text-destructive" />
                  )}
                  <div className="min-w-0">
                    <code className="font-mono">{r.methodName}</code>
                    {r.success ? (
                      <span className="text-muted-foreground"> · {r.latencyMs} ms</span>
                    ) : (
                      <span className="text-destructive break-words">
                        {' '}· {r.error || 'Connection failed'}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      <ResponsiveTable<DeviceConnectionMethod>
        columns={columns}
        rows={methods ?? []}
        rowKey={(r) => r.id}
        rowActions={(method) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => { e.stopPropagation(); }}
                aria-label="Actions"
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => { e.stopPropagation(); }}>
              <DropdownMenuItem
                onClick={() => { openEditModal(method); }}
                className="gap-2"
              >
                <Pencil className="w-4 h-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setDeleteTarget({
                    id: method.id,
                    label: `${method.sessionType.toUpperCase()} ${method.host}${method.port ? `:${String(method.port)}` : ''}`,
                  });
                }}
                className="gap-2 text-destructive focus:text-destructive"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        empty={
          <EmptyState
            variant="no-data"
            title="No connection methods configured"
            description="Add at least one connection method (SSH, Telnet, etc.) so the gateway can reach this device."
            action={
              <Button onClick={openCreateModal}>
                <Plus className="w-4 h-4" />
                Add method
              </Button>
            }
            size="inline"
          />
        }
      />

      <AppSheet
        open={isModalActive}
        onOpenChange={(open) => {
          if (!open) closeModal();
        }}
        title={isEditing ? 'Edit connection method' : 'Add connection method'}
        size="lg"
      >
        <AppSheet.Body>
          {submitError ? (
            <Alert variant="destructive" className="mb-4 relative">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{submitError}</AlertDescription>
              <button
                type="button"
                onClick={() => { setSubmitError(null); }}
                className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
                aria-label="Dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </Alert>
          ) : null}

          <form
            id="cm-form"
            onSubmit={(e) => { void handleSubmit(e); }}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="cm-type">Session type</Label>
              <Select
                value={formSessionType}
                onValueChange={(newType) => {
                  // `formSessionType` is still the previous type here (the state
                  // update below hasn't re-rendered yet).
                  const prevType = formSessionType;
                  setFormSessionType(newType);
                  setFormCredentialId(NO_CRED_SENTINEL);
                  if (newType === 'console') {
                    setFormTransportId(
                      consoleTransports.length > 0 ? consoleTransports[0].id : NEW_SSH_SENTINEL,
                    );
                    resetNewServerFields();
                  } else if (prevType === 'console') {
                    setFormTransportId(NO_TRANSPORT_SENTINEL);
                  }
                  if (newType === 'snmp' && deviceIp && !formHost) {
                    setFormHost(deviceIp);
                  }
                  // If the port still holds the previous type's default (or is
                  // blank), move it to the new type's default so it tracks the
                  // type — but leave a port the user deliberately customized.
                  // (DEFAULT_PORTS has no entry for netconf/console, so their
                  // lookups are undefined at runtime; String() handles that.)
                  const nextDefault = DEFAULT_PORTS[newType];
                  if (formPort === '' || formPort === String(DEFAULT_PORTS[prevType])) {
                    setFormPort(nextDefault ? String(nextDefault) : '');
                  }
                }}
              >
                <SelectTrigger id="cm-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ssh">SSH</SelectItem>
                  <SelectItem value="telnet">Telnet</SelectItem>
                  <SelectItem value="http">HTTP</SelectItem>
                  <SelectItem value="https">HTTPS</SelectItem>
                  <SelectItem value="console">Console</SelectItem>
                  <SelectItem value="snmp">SNMP</SelectItem>
                  <SelectItem value="netconf">NETCONF</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* HTTP(S) is the one transport that doesn't identify its own
                surface: a browsable UI and a machine API can both sit on
                https, on the same device, on different ports. Recording which
                one this is keeps them from collapsing into a single method and
                decides whether it backs the Web UI or eAPI/REST card. */}
            {isWebTransport ? (
              <div className="space-y-1.5">
                <Label htmlFor="cm-service">Surface</Label>
                <Select value={formService} onValueChange={setFormService}>
                  <SelectTrigger id="cm-service">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_ROLE_SENTINEL}>
                      Infer from vendor profile
                    </SelectItem>
                    <SelectItem value="cgi">Web UI (browsable)</SelectItem>
                    <SelectItem value="rest">REST / eAPI</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Which surface this endpoint serves. Leave on infer unless the
                  device exposes both.
                </p>
              </div>
            ) : null}

            {isConsole ? (
              <div className="space-y-1.5">
                <Label htmlFor="cm-server">Console server</Label>
                <Select
                  value={formTransportId}
                  onValueChange={(v) => {
                    setFormTransportId(v);
                    resetNewServerFields();
                  }}
                >
                  <SelectTrigger id="cm-server">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTransports.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} (
                        {t.transportType === 'ssh_tunnel' ? 'SSH' : 'Telnet'} — {t.host}:
                        {t.port})
                      </SelectItem>
                    ))}
                    <SelectItem value={NEW_SSH_SENTINEL}>+ New SSH console server</SelectItem>
                    <SelectItem value={NEW_TELNET_SENTINEL}>
                      + New Telnet console server
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {isCreatingNewServer
                    ? 'Fill in the console server details below. It will be created automatically when you submit.'
                    : 'The gateway will connect to this server to reach the device serial console.'}
                </p>
              </div>
            ) : null}

            {isConsole && isCreatingNewServer ? (
              <div className="rounded-md border border-border bg-bg-subtle p-4 space-y-3">
                <p className="text-sm font-semibold">
                  New {formTransportId === NEW_SSH_SENTINEL ? 'SSH' : 'Telnet'} console server
                </p>
                <div className="space-y-1.5">
                  <Label htmlFor="cm-server-name">Server name</Label>
                  <Input
                    id="cm-server-name"
                    placeholder={
                      newServerHost
                        ? `Console: ${newServerHost}`
                        : 'e.g., Console: ts1.example.com'
                    }
                    value={newServerName}
                    onChange={(e) => { setNewServerName(e.target.value); }}
                  />
                  <p className="text-xs text-muted-foreground">
                    Optional — defaults to "Console: {'{'} host {'}'}"
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cm-server-host">Host</Label>
                  <Input
                    id="cm-server-host"
                    placeholder="e.g., console-server.example.com"
                    value={newServerHost}
                    onChange={(e) => { setNewServerHost(e.target.value); }}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cm-server-port">Port</Label>
                  <Input
                    id="cm-server-port"
                    type="number"
                    placeholder={formTransportId === NEW_SSH_SENTINEL ? '22' : '23'}
                    value={newServerPort}
                    onChange={(e) => { setNewServerPort(e.target.value); }}
                    min={1}
                    max={65535}
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave blank for default ({formTransportId === NEW_SSH_SENTINEL ? '22' : '23'})
                  </p>
                </div>
                {formTransportId === NEW_SSH_SENTINEL ? (
                  <div className="space-y-1.5">
                    <Label htmlFor="cm-server-cred">Server credential</Label>
                    <CredentialSelect
                      id="cm-server-cred"
                      value={newServerCredentialId === NO_CRED_SENTINEL ? null : newServerCredentialId}
                      onChange={(id) => { setNewServerCredentialId(id ?? NO_CRED_SENTINEL); }}
                      regionId={regionId}
                      divisionId={divisionId}
                      types={SSH_CREDENTIAL_TYPES}
                      optional
                    />
                    <p className="text-xs text-muted-foreground">
                      Login credential for the console server itself.
                    </p>
                  </div>
                ) : null}
              </div>
            ) : null}

            {showHostPort ? (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="cm-host">Host</Label>
                  <Input
                    id="cm-host"
                    placeholder="e.g., 192.168.1.1 or switch.example.com"
                    value={formHost}
                    onChange={(e) => { setFormHost(e.target.value); }}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cm-port">Port</Label>
                  <Input
                    id="cm-port"
                    type="number"
                    placeholder={String(DEFAULT_PORTS[formSessionType] ?? '')}
                    value={formPort}
                    onChange={(e) => { setFormPort(e.target.value); }}
                    min={1}
                    max={65535}
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave blank for default (
                    {DEFAULT_PORTS[formSessionType]
                      ? String(DEFAULT_PORTS[formSessionType])
                      : 'none'}
                    )
                  </p>
                </div>
              </>
            ) : null}

            {isConsole && consoleTransportIsSsh ? (
              <div className="space-y-1.5">
                <Label htmlFor="cm-command">Command</Label>
                <Input
                  id="cm-command"
                  placeholder="e.g., minicom --device /dev/ttyS1"
                  value={formCommand}
                  onChange={(e) => { setFormCommand(e.target.value); }}
                />
                <p className="text-xs text-muted-foreground">
                  Optional. Exec this command on the console server instead of opening a
                  shell.
                </p>
              </div>
            ) : null}

            {!isConsole && availableTransports.length > 0 ? (
              <div className="space-y-1.5">
                <Label htmlFor="cm-transport">Transport</Label>
                <Select value={formTransportId} onValueChange={setFormTransportId}>
                  <SelectTrigger id="cm-transport">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_TRANSPORT_SENTINEL}>None (direct)</SelectItem>
                    {availableTransports.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} ({t.transportType})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="cm-cred">
                {isConsole ? 'Device credential' : isSnmp ? 'SNMP credential' : 'Credential'}
              </Label>
              <CredentialSelect
                id="cm-cred"
                value={formCredentialId === NO_CRED_SENTINEL ? null : formCredentialId}
                onChange={(id) => { setFormCredentialId(id ?? NO_CRED_SENTINEL); }}
                regionId={regionId}
                divisionId={divisionId}
                types={credentialTypesFor(formSessionType)}
                optional
              />
              {isConsole ? (
                <p className="text-xs text-muted-foreground">
                  Login credential for the device at the serial console (not the console
                  server).
                </p>
              ) : null}
              {isSnmp ? (
                <p className="text-xs text-muted-foreground">
                  Only SNMPv2 Community and SNMPv3 credentials are shown.
                </p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cm-priority">Priority</Label>
              <Input
                id="cm-priority"
                type="number"
                value={formPriority}
                onChange={(e) => { setFormPriority(e.target.value); }}
                min={0}
              />
              <p className="text-xs text-muted-foreground">Lower values are tried first.</p>
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={formEnabled}
                onCheckedChange={(v) => { setFormEnabled(v === true); }}
              />
              Enabled
            </label>
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button variant="outline" onClick={closeModal} disabled={isMutating}>
            Cancel
          </Button>
          <Button type="submit" form="cm-form" disabled={isMutating}>
            {isMutating ? 'Saving…' : isEditing ? 'Save changes' : 'Add'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      <ConfirmationModal
        isActive={!!deleteTarget}
        title="Delete connection method"
        message={`Are you sure you want to delete the connection method "${deleteTarget?.label ?? ''}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={(e) => {
          if (!deleteTarget) return;
          // A refused delete leaves the row in place — a controller-owned method
          // answers 409, naming the integration that owns it and the way out.
          // Closing the dialog would report the delete as done, so preventing
          // the default keeps Radix from shutting it and hands the close to
          // `onSuccess`. `submitError` is not usable here: it renders inside the
          // edit sheet, which is shut whenever this dialog is open.
          e.preventDefault();
          deleteMutation.mutate(deleteTarget.id, {
            onSuccess: () => { setDeleteTarget(null); },
            onError: (err) => {
              toast.error(apiErrorMessage(err, 'Failed to delete connection method.'));
            },
          });
        }}
        onCancel={() => { setDeleteTarget(null); }}
        isLoading={deleteMutation.isPending}
        isDanger
      />
    </section>
  );
}
